import { useQueryClient } from '@tanstack/react-query'
import { Button, notification, Tooltip } from 'antd'
import BigNumber from 'bignumber.js'
import Identicon from 'identicon.js'
import numeral from 'numeral'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { parseEther } from 'viem'
import {
  useAccount,
  useChainId,
  useConnect,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi'
import { injected } from 'wagmi/connectors'

import BackImg from '@/assets/back.png'
import BackImg_Light from '@/assets/back_light.png'
import error_icon from '@/assets/error_icon.png'
import success_icon from '@/assets/success_icon.png'
import ToolTipCus from '@/components/Tooltips'
import { BridgeDataToolTip, config } from '@/config'
import { useLocalStorage } from '@/context/localstorage-context'
import DelegateConfirm from '@/pages/Bridge/delegate-confirm'
import DelegateModal from '@/pages/Bridge/delegate-modal'
import type {
  StoremanDelegatorInfoType,
  StoremanInfoType,
} from '@/types/storeman-type'
import { fromWei1 } from '@/utils'
import { useQueryStoremanConf, useStoremanInfo } from '@/utils/queries'

import StatusModal from './status-modal'
import { useStoremanDelegatorInfo } from './storeman-data'
import {
  BackBtn,
  Btn,
  BtnArea,
  Data,
  Data1,
  DataTitle,
  DetailAddr,
  DetailAddr1,
  DetailAddr2,
  DetailInfo,
  Icon,
  InfoRow,
  Line,
  LineCloumn,
  LineRow,
  Link,
  MainBody,
  MyActions,
  MyDelegate,
} from './stroeman-detail.module'

export default function StoremanDetail() {
  const chainId = useChainId()
  const location = useLocation()
  const [showModal, setShowModal] = useState(false)
  const [showTopUpModal, setShowTopUpModal] = useState(false)
  const [showTransactionSentModal, setShowTransactionSentModal] =
    useState(false)
  const [showWithdrawConfirmModal, setShowWithdrawConfirmModal] =
    useState(false)
  const [showClaimConfirmModal, setShowClaimConfirmModal] = useState(false)
  const storemanLists = useStoremanInfo()
  const storemanDelegatorInfo = useStoremanDelegatorInfo()
  const storemanConf = useQueryStoremanConf()
  const { data: hash, writeContract } = useWriteContract()
  const { isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  })

  const { connect } = useConnect()
  const { status } = useAccount()

  const queryClient = useQueryClient()
  const { theme } = useLocalStorage()

  const isdark = useMemo(() => theme === 'dark', [theme])
  const StoremanContractConfig = config[chainId]['StoremanContract']

  const storemanInfo = useMemo(() => {
    if (storemanLists && storemanLists.length > 0) {
      const wkAddr = location.pathname.split('/storemandetail/')[1]
      const storeman = storemanLists.find(
        (v: StoremanInfoType) =>
          v.wkAddr.toLowerCase() === wkAddr.toLowerCase(),
      )
      let tmp: any
      if (storeman) {
        tmp = Object.assign({}, storeman)
        tmp.myDeposit = '0'
        tmp.myIncentive = '0'
        tmp.quota = '0'
        storemanDelegatorInfo.forEach((j: StoremanDelegatorInfoType) => {
          if (storeman.wkAddr.toLowerCase() === j.wkAddr.toLowerCase()) {
            tmp.myQuited = j.quited
            tmp.myDeposit = j.deposit.toString()
            tmp.myIncentive = j.incentive.toString()
            tmp.canDelegateOut = j.canDelegateOut
            tmp.canDelegateClaim = j.canDelegateClaim
          }
        })
        if (storemanConf) {
          const quota = new BigNumber(fromWei1(storeman.deposit))
            .plus(fromWei1(storeman.partnerDeposit || '0'))
            .multipliedBy(storemanConf.delegationMulti || '1')
            .minus(fromWei1(storeman.delegateDeposit || '0'))
            .toString(10)
          const quotaT = new BigNumber(quota).lt('0') ? '0' : quota
          tmp.quota = numeral(quotaT)
            .format('0,0.00')
            .replace(/\.?0+$/, '')
        }
      } else {
        if (storemanDelegatorInfo.length === 0) return {}
        tmp = storemanDelegatorInfo.find(
          (i: StoremanDelegatorInfoType) =>
            i.wkAddr.toLowerCase() === wkAddr.toLowerCase(),
        )
        tmp.name = tmp.wkAddr
        tmp.storeman = '--'
        tmp.activity = '0'
        tmp.totalDeposit = '0'
        tmp.delegateFee = '0'
        tmp.powerWeight = '0'
        tmp.status = 'Finished'
        tmp.imgdata = `data:image/png;base64,${new Identicon(
          tmp.wkAddr,
        ).toString()}`
        tmp.myDeposit = tmp.deposit.toString()
        tmp.myIncentive = tmp.incentive.toString()
      }
      return tmp || {}
    } else {
      return {}
    }
  }, [location.pathname, storemanConf, storemanDelegatorInfo, storemanLists])

  const storeman = useMemo(() => {
    if (storemanLists && storemanLists.length > 0) {
      const tmp = storemanLists.find(
        (v: StoremanInfoType) =>
          v.wkAddr === location.pathname.split('/storemandetail/')[1],
      )
      return tmp
    } else {
      return null
    }
  }, [location.pathname, storemanLists])

  useEffect(() => {
    if (hash && isConfirmed) {
      notification.success({
        message: 'Transaction completed',
        description: <Link txHash={hash} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={success_icon} alt="notification" />,
      })
      queryClient.invalidateQueries({
        queryKey: ['useStoremanInfo'],
      })
      setShowTopUpModal(false)
      setShowWithdrawConfirmModal(false)
      setShowClaimConfirmModal(false)
    }
  }, [isConfirmed, hash])

  const storemanDelegateIn = (amount: string) =>
    writeContract(
      {
        address: StoremanContractConfig.addr,
        abi: StoremanContractConfig.abi,
        functionName: 'delegateIn',
        args: [storemanInfo.wkAddr],
        value: parseEther(amount),
      },
      {
        onError: () => {
          notification.error({
            message: 'Transaction Sent Failed',
            description: <Link txHash={hash || ''} />,
            className: `${
              isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'
            }`,
            icon: <img width={30} src={error_icon} alt="notification" />,
          })
          setShowTopUpModal(false)
        },
      },
    )

  const storemanWithdraw = () =>
    writeContract(
      {
        address: StoremanContractConfig.addr,
        abi: StoremanContractConfig.abi,
        functionName: 'delegateOut',
        args: [storemanInfo.wkAddr],
      },
      {
        onError: () => {
          notification.error({
            message: 'Transaction Sent Failed',
            description: <Link txHash={hash || ''} />,
            className: `${
              isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'
            }`,
            icon: <img width={30} src={error_icon} alt="notification" />,
          })
          setShowWithdrawConfirmModal(false)
        },
      },
    )

  const storemanClaim = () => {
    writeContract(
      {
        address: StoremanContractConfig.addr,
        abi: StoremanContractConfig.abi,
        functionName: `${
          storemanInfo.canDelegateClaim
            ? 'delegateClaim'
            : 'delegateIncentiveClaim'
        }`,
        args: [storemanInfo.wkAddr],
      },
      {
        onError: () => {
          notification.error({
            message: 'Transaction Sent Failed',
            description: <Link txHash={hash || ''} />,
            className: `${
              isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'
            }`,
            icon: <img width={30} src={error_icon} alt="notification" />,
          })
          setShowClaimConfirmModal(false)
        },
      },
    )
  }

  const handleTopUp = async () => {
    if (status === 'connected') {
      setShowTopUpModal(true)
    } else {
      connect({ connector: injected() })
      setShowTopUpModal(true)
    }
  }

  const handleWithdraw = async () => {
    if (status === 'connected') {
      setShowWithdrawConfirmModal(true)
    } else {
      connect({ connector: injected() })
      setShowWithdrawConfirmModal(true)
    }
  }

  const handleClaim = async () => {
    if (status === 'connected') {
      setShowClaimConfirmModal(true)
    } else {
      connect({ connector: injected() })
      setShowClaimConfirmModal(true)
    }
  }

  const goBack = () => {
    window.history.back()
  }

  const unclaimable = useMemo(() => {
    if (storeman && storemanInfo) {
      const tmp = storemanInfo.canDelegateClaim
        ? new BigNumber(storemanInfo.myIncentive)
            .plus(storemanInfo.myDeposit)
            .toString(10)
        : storemanInfo.myIncentive
      return numeral(tmp)
        .format('0,0.0000')
        .replace(/\.?0+$/, '')
    } else if (
      !storeman &&
      storemanInfo &&
      storemanInfo.status === 'Finished'
    ) {
      const tmp = new BigNumber(storemanInfo.myIncentive)
        .plus(storemanInfo.myDeposit)
        .toString(10)
      return numeral(tmp)
        .format('0,0.0000')
        .replace(/\.?0+$/, '')
    } else {
      return '0'
    }
  }, [storeman, storemanInfo, storemanInfo.canDelegateClaim])

  const enableTopupButton = useMemo(() => {
    if (storeman && storemanInfo && !storemanInfo.myQuited) return false
    return true
  }, [storeman, storemanInfo])

  const disableTopupTitle = useMemo(() => {
    if (storeman && storemanInfo && storemanInfo.myQuited) {
      return 'The delegated WAN assets are in the exit process and cannot be topped up.'
    } else {
      return ''
    }
  }, [storeman, storemanInfo])

  const enableExitButton = useMemo(() => {
    return (
      !storeman ||
      !storemanInfo ||
      !(storemanInfo.canDelegateOut && !storemanInfo.myQuited) ||
      storemanInfo.canDelegateClaim
    )
  }, [storeman, storemanInfo])

  const disableExitTitle = useMemo(() => {
    if (storeman && storemanInfo && storemanInfo.myQuited) {
      return 'You have already exited, no need to exit again.'
    } else if (
      storeman &&
      storemanInfo &&
      !storemanInfo.myQuited &&
      !storemanInfo.canDelegateOut
    ) {
      return "Exiting is unavailable from the 1st to the 9th during the Storeman group's cycle switch. Please try again after the 9th."
    } else {
      return ''
    }
  }, [storeman, storemanInfo])

  return (
    <MainBody>
      <BackBtn onClick={goBack}>
        <div
          className="bg-white dark:bg-common-1"
          style={{ width: '32px', borderRadius: '5px' }}
        >
          <img src={isdark ? BackImg : BackImg_Light} alt="back" width={32} />
        </div>
      </BackBtn>
      <DetailAddr className="bg-white dark:bg-dark-222">
        <DetailAddr1>
          <Icon src={storemanInfo.imgdata} alt="" />
        </DetailAddr1>
        <DetailAddr2>
          <p
            className="text-light-0 dark:text-white"
            style={{
              fontSize: '24px',
              wordBreak: 'break-word',
              textAlign: 'left',
            }}
          >
            {storemanInfo.name}
          </p>
          <p
            className="text-light-333 dark:text-dark-0"
            style={{
              color: '#757575',
              wordBreak: 'break-word',
              textAlign: 'left',
            }}
          >
            {storemanInfo.wkAddr}
          </p>
        </DetailAddr2>
      </DetailAddr>
      <DetailInfo height={'26.5rem'} className="bg-white dark:bg-dark-222">
        {/********* / Part1  *********/}
        <InfoRow>
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Total Staked Amount
              <ToolTipCus title={BridgeDataToolTip[0]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {storemanInfo.totalDeposit || '--'}
              </span>
              <span>WAN</span>
            </p>
          </Data>
          <LineCloumn isdark={isdark} />
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Remaining Quota
              <ToolTipCus title={BridgeDataToolTip[1]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {storemanInfo.quota || '--'}
              </span>
              <span>WAN</span>
            </p>
          </Data>
        </InfoRow>
        <LineRow isdark={isdark} />

        {/********* / Part2  *********/}
        <InfoRow>
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Activity
              <ToolTipCus title={BridgeDataToolTip[2]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {storemanInfo.activity || '--'}
              </span>
            </p>
          </Data>
          <LineCloumn isdark={isdark} />
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Delegation Fee(%)
              <ToolTipCus title={BridgeDataToolTip[3]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {isNaN(storemanInfo.delegateFee)
                  ? '--'
                  : storemanInfo.delegateFee / 100 + '%'}
              </span>
            </p>
          </Data>
        </InfoRow>
        <LineRow isdark={isdark} />

        {/********* / Part3  *********/}
        <InfoRow>
          <Data>
            <DataTitle className="text-light-444 dark:text-dark-0">
              Status
              <ToolTipCus title={BridgeDataToolTip[4]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {storemanInfo.status || '--'}
              </span>
            </p>
          </Data>
          <LineCloumn isdark={isdark} />
          <Data>
            <DataTitle>
              Auto Renew
              <ToolTipCus title={BridgeDataToolTip[5]} />
            </DataTitle>
            <p>
              <span className="text-light-0 dark:text-white">
                {storemanInfo && storemanInfo.autoRenew !== undefined
                  ? storemanInfo.autoRenew.toString()
                  : '--'}
              </span>
            </p>
          </Data>
        </InfoRow>
      </DetailInfo>
      {new BigNumber(storemanInfo.myDeposit).eq('0') ? (
        <Btn>
          <Button block onClick={handleTopUp}>
            Delegate
          </Button>
        </Btn>
      ) : (
        <MyDelegate className="bg-white dark:bg-dark-222">
          <InfoRow count={2} direction="column">
            <Data1>
              <DataTitle className="text-light-444 dark:text-dark-0">
                My Rewards
              </DataTitle>
              <div>
                <span className="text-light-111 dark:text-white">
                  {numeral(storemanInfo.myIncentive)
                    .format('0,0.0000')
                    .replace(/\.?0+$/, '')}
                </span>
                <span>WAN</span>
                <Line />
                <span style={{ fontWeight: 400 }}>Claimable: </span>
                <span style={{ fontWeight: 400, color: '#228CB7' }}>
                  {unclaimable}
                </span>
                <span style={{ marginLeft: '5px' }}>WAN</span>
              </div>
            </Data1>
          </InfoRow>
          <LineRow isdark={isdark} />
          <InfoRow count={2} direction="column">
            <Data1>
              <DataTitle className="text-light-444 dark:text-dark-0">
                My Delegations
              </DataTitle>
              <MyActions
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <span>
                  <span className="text-light-111 dark:text-white">
                    {numeral(storemanInfo.myDeposit)
                      .format('0,0.0000')
                      .replace(/\.?0+$/, '')}
                  </span>
                  <span className="text-light-444 dark:text-dark-0">WAN</span>
                </span>
                <BtnArea isdark={isdark}>
                  <Tooltip title={enableTopupButton ? disableTopupTitle : ''}>
                    <Button disabled={enableTopupButton} onClick={handleTopUp}>
                      Top-up
                    </Button>
                  </Tooltip>
                  <Tooltip title={enableExitButton ? disableExitTitle : ''}>
                    <Button
                      disabled={enableExitButton}
                      onClick={handleWithdraw}
                    >
                      Exit
                    </Button>
                  </Tooltip>
                  <Button onClick={handleClaim}>Claim</Button>
                </BtnArea>
              </MyActions>
            </Data1>
          </InfoRow>
        </MyDelegate>
      )}
      {showModal && (
        <DelegateModal
          type="Delegate"
          visible={showModal}
          onCancel={() => setShowModal(false)}
          storemanInfo={storemanInfo}
          action={() => {}}
        />
      )}
      {showTopUpModal && (
        <DelegateModal
          type={
            new BigNumber(storemanInfo.myDeposit).eq('0')
              ? 'Delegate'
              : 'Top-up'
          }
          visible={showTopUpModal}
          onCancel={() => setShowTopUpModal(false)}
          storemanInfo={storemanInfo}
          action={(v: string) => storemanDelegateIn(v)}
        />
      )}
      {showWithdrawConfirmModal && (
        <DelegateConfirm
          visible={showWithdrawConfirmModal}
          onCancel={() => setShowWithdrawConfirmModal(false)}
          storemanInfo={storemanInfo}
          action={storemanWithdraw}
          amount={storemanInfo.myDeposit}
          type="Undelegate WAN"
        />
      )}
      {showClaimConfirmModal && (
        <DelegateConfirm
          visible={showClaimConfirmModal}
          onCancel={() => setShowClaimConfirmModal(false)}
          storemanInfo={storemanInfo}
          action={storemanClaim}
          amount={unclaimable}
          type="Claim"
        />
      )}
      {showTransactionSentModal && (
        <StatusModal
          type="success"
          visible={true}
          onCancel={() => setShowTransactionSentModal(false)}
        />
      )}
    </MainBody>
  )
}
